import { useEffect, useState } from "react";
import { endpoints, fmtUSD } from "@/lib/api";
import { BarChart3 } from "lucide-react";

const PALETTE = ["bg-orange-500", "bg-zinc-800", "bg-amber-400", "bg-sky-500", "bg-emerald-500", "bg-rose-400", "bg-zinc-400"];

export default function PlaybookAnalytics() {
  const [data, setData] = useState(null);
  useEffect(() => { endpoints.playbookAnalytics().then(setData).catch(() => {}); }, []);
  if (!data) return null;
  const rows = data.playbooks;
  const withTcv = rows.filter((r) => r.released_tcv_usd > 0);

  return (
    <div className="border border-zinc-200 bg-white p-6 mb-10" data-testid="playbook-analytics">
      <div className="flex items-start justify-between gap-6 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1"><BarChart3 className="w-4 h-4 text-orange-600" /><span className="tx-overline">Analytics · released CPPO value by playbook</span></div>
          <p className="text-[12px] text-zinc-500">Which incentive structures turn agent runs into released private offers — and how much TCV they carry.</p>
        </div>
        <div className="text-right shrink-0">
          <div className="tx-overline">Released TCV</div>
          <div className="font-heading font-black text-3xl text-orange-600" data-testid="analytics-total-tcv">{fmtUSD(data.total_released_tcv_usd)}</div>
          <div className="font-mono text-[11px] text-zinc-500">{data.total_runs} runs</div>
        </div>
      </div>

      {withTcv.length > 0 && (
        <div className="flex h-3 w-full overflow-hidden rounded-sm mb-2" data-testid="analytics-share-bar">
          {withTcv.map((r, i) => (
            <div key={r.key} title={`${r.name} · ${r.tcv_share_pct}%`} style={{ width: `${r.tcv_share_pct}%` }} className={`${PALETTE[i % PALETTE.length]} transition-all`} />
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-x-5 gap-y-1 mb-5">
        {withTcv.map((r, i) => (
          <span key={r.key} className="flex items-center gap-1.5 font-mono text-[10px] text-zinc-600">
            <span className={`w-2 h-2 rounded-sm ${PALETTE[i % PALETTE.length]}`} />{r.name} · {r.tcv_share_pct}%
          </span>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[12px]" data-testid="analytics-table">
          <thead className="border-b border-zinc-200 text-left">
            <tr className="tx-overline">
              <th className="py-2 pr-4 font-normal">Playbook</th>
              <th className="py-2 pr-4 font-normal text-right">Runs</th>
              <th className="py-2 pr-4 font-normal text-right">Drafted</th>
              <th className="py-2 pr-4 font-normal text-right">Awaiting</th>
              <th className="py-2 pr-4 font-normal text-right">Released</th>
              <th className="py-2 pr-4 font-normal text-right">Conversion</th>
              <th className="py-2 pr-4 font-normal text-right">Avg markup</th>
              <th className="py-2 font-normal text-right">Released TCV</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {rows.map((r) => (
              <tr key={r.key} data-testid={`analytics-row-${r.key}`} className="hover:bg-zinc-50">
                <td className="py-2.5 pr-4"><span className="text-zinc-900">{r.name}</span> <span className="font-mono text-[10px] text-zinc-400 ml-1">{r.key}</span></td>
                <td className="py-2.5 pr-4 text-right font-mono text-zinc-700">{r.runs}</td>
                <td className="py-2.5 pr-4 text-right font-mono text-zinc-700">{r.drafted}</td>
                <td className="py-2.5 pr-4 text-right font-mono text-amber-700">{r.awaiting_approval}</td>
                <td className="py-2.5 pr-4 text-right font-mono text-emerald-700">{r.released}</td>
                <td className="py-2.5 pr-4 text-right font-mono text-zinc-700">{r.conversion_pct}%</td>
                <td className="py-2.5 pr-4 text-right font-mono text-zinc-700">{r.avg_markup_percent != null ? `${r.avg_markup_percent}%` : "—"}</td>
                <td className="py-2.5 text-right font-heading font-black text-zinc-900">{fmtUSD(r.released_tcv_usd)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="py-6 text-zinc-500">No runs yet — dispatch an agent run or hand off a deal.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
